<template>
  <v-dialog
    :value="value"
    @input="$emit('input', $event)"
    max-width="900"
    scrollable
  >
    <v-card>
      <v-card-title class="d-flex align-center py-3">
        <v-icon class="mr-2">mdi-api</v-icon>
        API Data Sources
        <v-btn icon x-small class="ml-1" @click="showInfo = true">
          <v-icon small color="info">mdi-information-outline</v-icon>
        </v-btn>
        <v-spacer />
        <v-btn small color="primary" @click="$emit('create')">
          <v-icon left small>mdi-plus</v-icon> New Source
        </v-btn>
        <v-btn icon small class="ml-2" @click="$emit('input', false)">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-divider />

      <v-card-text class="pa-0">
        <v-data-table
          :headers="headers"
          :items="dataSources"
          :loading="loading"
          dense
          :items-per-page="10"
          class="api-sources-table"
        >
          <!-- Status -->
          <template v-slot:[`item.status`]="{ item }">
            <v-chip
              x-small
              :color="getStatusColor(item.status)"
              dark
            >
              {{ getStatusText(item.status) }}
            </v-chip>
          </template>

          <!-- Schedule -->
          <template v-slot:[`item.scheduleFrequency`]="{ item }">
            <span class="text-caption">
              {{ getFrequencyText(item.scheduleFrequency) }}
            </span>
          </template>

          <!-- Last Run -->
          <template v-slot:[`item.lastExecutionAt`]="{ item }">
            <span v-if="item.lastExecutionAt" class="text-caption">
              {{ formatDate(item.lastExecutionAt) }}
            </span>
            <span v-else class="text-caption text--disabled">Never</span>
          </template>

          <!-- Last Status -->
          <template v-slot:[`item.lastExecutionStatus`]="{ item }">
            <v-icon
              v-if="item.lastExecutionStatus != null"
              x-small
              :color="getExecutionInfo(item.lastExecutionStatus).color"
            >
              {{ getExecutionInfo(item.lastExecutionStatus).icon }}
            </v-icon>
          </template>

          <!-- Actions -->
          <template v-slot:[`item.actions`]="{ item }">
            <v-tooltip bottom>
              <template v-slot:activator="{ on }">
                <v-btn icon x-small v-on="on" @click="triggerPull(item)" :loading="triggeringId === item.id">
                  <v-icon small color="primary">mdi-sync</v-icon>
                </v-btn>
              </template>
              <span>Sync Now</span>
            </v-tooltip>

            <v-tooltip bottom>
              <template v-slot:activator="{ on }">
                <v-btn icon x-small v-on="on" @click="toggleSchedule(item)">
                  <v-icon small :color="item.status === 1 ? 'warning' : 'success'">
                    {{ item.status === 1 ? 'mdi-pause' : 'mdi-play-circle' }}
                  </v-icon>
                </v-btn>
              </template>
              <span>{{ item.status === 1 ? 'Pause' : 'Resume' }}</span>
            </v-tooltip>

            <v-tooltip bottom>
              <template v-slot:activator="{ on }">
                <v-btn icon x-small v-on="on" @click="$emit('view-history', item)">
                  <v-icon small color="info">mdi-history</v-icon>
                </v-btn>
              </template>
              <span>History</span>
            </v-tooltip>

            <v-tooltip bottom>
              <template v-slot:activator="{ on }">
                <v-btn icon x-small v-on="on" @click="$emit('edit', item)">
                  <v-icon small color="accent">mdi-pencil</v-icon>
                </v-btn>
              </template>
              <span>Edit</span>
            </v-tooltip>

            <v-tooltip bottom>
              <template v-slot:activator="{ on }">
                <v-btn icon x-small v-on="on" @click="confirmDelete(item)">
                  <v-icon small color="error">mdi-delete</v-icon>
                </v-btn>
              </template>
              <span>Delete</span>
            </v-tooltip>
          </template>

          <!-- Empty state -->
          <template v-slot:no-data>
            <div class="text-center py-6">
              <v-icon size="40" color="grey lighten-1">mdi-api-off</v-icon>
              <div class="text-body-2 text--secondary mt-2">
                No API data sources configured yet.
              </div>
              <v-btn small text color="primary" class="mt-2" @click="$emit('create')">
                <v-icon left small>mdi-plus</v-icon> Create First Source
              </v-btn>
            </div>
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>

    <!-- Info Modal -->
    <v-dialog v-model="showInfo" max-width="550" scrollable>
      <v-card>
        <v-card-title class="py-3">
          <v-icon class="mr-2" color="info">mdi-information-outline</v-icon>
          About API Data Sources
          <v-spacer />
          <v-btn icon small @click="showInfo = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-divider />

        <v-card-text class="pt-4">
          <p class="text-body-2 mb-3">
            API Data Sources allow you to automatically import and sync records
            from external REST APIs on a scheduled or on-demand basis.
          </p>

          <div class="text-subtitle-2 font-weight-bold mb-2">Available Actions</div>
          <v-list dense class="pa-0 mb-3">
            <v-list-item class="px-0">
              <v-list-item-icon class="mr-3"><v-icon small color="primary">mdi-sync</v-icon></v-list-item-icon>
              <v-list-item-content>
                <v-list-item-title class="text-body-2">Sync Now</v-list-item-title>
                <v-list-item-subtitle>Immediately trigger a data pull from the configured API endpoint.</v-list-item-subtitle>
              </v-list-item-content>
            </v-list-item>
            <v-list-item class="px-0">
              <v-list-item-icon class="mr-3"><v-icon small color="warning">mdi-pause</v-icon></v-list-item-icon>
              <v-list-item-content>
                <v-list-item-title class="text-body-2">Pause</v-list-item-title>
                <v-list-item-subtitle>Suspend scheduled syncs. The data source remains configured but won't run automatically.</v-list-item-subtitle>
              </v-list-item-content>
            </v-list-item>
            <v-list-item class="px-0">
              <v-list-item-icon class="mr-3"><v-icon small color="success">mdi-play-circle</v-icon></v-list-item-icon>
              <v-list-item-content>
                <v-list-item-title class="text-body-2">Resume</v-list-item-title>
                <v-list-item-subtitle>Re-enable scheduled syncs for a paused data source.</v-list-item-subtitle>
              </v-list-item-content>
            </v-list-item>
            <v-list-item class="px-0">
              <v-list-item-icon class="mr-3"><v-icon small color="info">mdi-history</v-icon></v-list-item-icon>
              <v-list-item-content>
                <v-list-item-title class="text-body-2">Execution History</v-list-item-title>
                <v-list-item-subtitle>View a log of all past sync runs including status, duration, records imported, and any errors.</v-list-item-subtitle>
              </v-list-item-content>
            </v-list-item>
            <v-list-item class="px-0">
              <v-list-item-icon class="mr-3"><v-icon small color="accent">mdi-pencil</v-icon></v-list-item-icon>
              <v-list-item-content>
                <v-list-item-title class="text-body-2">Edit</v-list-item-title>
                <v-list-item-subtitle>Modify the API configuration, authentication, field mappings, or schedule.</v-list-item-subtitle>
              </v-list-item-content>
            </v-list-item>
            <v-list-item class="px-0">
              <v-list-item-icon class="mr-3"><v-icon small color="error">mdi-delete</v-icon></v-list-item-icon>
              <v-list-item-content>
                <v-list-item-title class="text-body-2">Delete</v-list-item-title>
                <v-list-item-subtitle>Permanently remove the data source, its schedule, and all execution history.</v-list-item-subtitle>
              </v-list-item-content>
            </v-list-item>
          </v-list>
        </v-card-text>

        <v-divider />

        <v-card-actions>
          <v-spacer />
          <v-btn text small @click="showInfo = false">Got it</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-dialog>
</template>

<script>
import {
  getDataSourceStatusColor,
  getDataSourceStatusText,
  getScheduleFrequencyText,
  getExecutionStatusInfo,
} from '@/features/Share/config/apiDataSourceEnums';

export default {
  name: 'SharedApiDataSourceList',
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    dataSources: {
      type: Array,
      default: () => [],
    },
    loading: {
      type: Boolean,
      default: false,
    },
    /** Service object with triggerPull(id), toggleSchedule(id, enabled), deleteDataSource(id) */
    apiService: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      showInfo: false,
      triggeringId: null,
      headers: [
        { text: 'Name', value: 'name', width: '180px' },
        { text: 'Status', value: 'status', width: '90px', sortable: false },
        { text: 'Schedule', value: 'scheduleFrequency', width: '100px', sortable: false },
        { text: 'Last Run', value: 'lastExecutionAt', width: '140px' },
        { text: '', value: 'lastExecutionStatus', width: '40px', sortable: false },
        { text: 'Actions', value: 'actions', width: '180px', sortable: false, align: 'end' },
      ],
    };
  },
  methods: {
    getStatusColor: getDataSourceStatusColor,
    getStatusText: getDataSourceStatusText,
    getFrequencyText: getScheduleFrequencyText,
    getExecutionInfo: getExecutionStatusInfo,

    async triggerPull(item) {
      this.triggeringId = item.id;
      try {
        await this.apiService.triggerPull(item.id);
        this.$dialog.notify.success(`Pull triggered for "${item.name}"`);
        this.$emit('refresh');
      } catch (err) {
        this.$dialog.notify.error(
          err.response?.data?.message || 'Failed to trigger pull'
        );
      } finally {
        this.triggeringId = null;
      }
    },

    async toggleSchedule(item) {
      const newEnabled = item.status !== 1;
      try {
        await this.apiService.toggleSchedule(item.id, newEnabled);
        this.$emit('refresh');
      } catch (err) {
        this.$dialog.notify.error('Failed to toggle schedule');
      }
    },

    confirmDelete(item) {
      this.$dialog.confirm({
        text: `Delete data source "${item.name}"? This will also remove its scheduled job and execution history.`,
        title: 'Delete Data Source',
        actions: {
          false: 'Cancel',
          true: { text: 'Delete', color: 'error' },
        },
      }).then(async confirmed => {
        if (confirmed) {
          try {
            await this.apiService.deleteDataSource(item.id);
            this.$dialog.notify.success('Data source deleted');
            this.$emit('refresh');
          } catch (err) {
            this.$dialog.notify.error('Failed to delete data source');
          }
        }
      });
    },

    formatDate(dateStr) {
      if (!dateStr) return '';
      const d = new Date(dateStr);
      return d.toLocaleDateString() + ' ' + d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    },
  },
};
</script>
